import { Injectable } from '@angular/core';
import { NGXLogger as LoggerService } from 'ngx-logger';

import { UserService } from '../../user/user.service';
import {
    getEffectiveLang,
    getLangPref,
    isLangDismissed,
    LangCode,
    setLangDismissed,
    setLangPref,
} from './lang-pref';

/**
 * Angular-Fassade um das framework-freie `lang-pref`-Modul. Sie fuegt die
 * Kopplung an den eingeloggten User hinzu: jede EXPLIZITE Sprachwahl schreibt
 * zusaetzlich `DbUser.language`, damit Deadline-/Reminder-Mails der aktuellen
 * statt der bei Account-Anlage eingefrorenen Sprache folgen.
 *
 * Die Gegenrichtung (DbUser.language → langPref beim Login) sitzt bewusst in
 * `UserService.subscribeDbUser()` und nutzt das lang-pref-Modul direkt — so
 * bleibt die Abhaengigkeit einseitig (Service → UserService) und es entsteht
 * kein DI-Zyklus.
 */
@Injectable({
    providedIn: 'root',
})
export class LanguagePreferenceService {
    constructor(
        private userService: UserService,
        private logger: LoggerService,
    ) { }

    /** Explizit gewaehlte Sprache, oder null. */
    public get preference(): LangCode | null {
        return getLangPref();
    }

    /** Praeferenz → Browsersprache (falls implementiert) → 'en'. */
    public get effectiveLanguage(): LangCode {
        return getEffectiveLang();
    }

    /**
     * Eine explizite Sprachwahl festhalten. Navigiert NICHT — der Aufrufer
     * entscheidet, wie gewechselt wird (i. d. R. `NavigationService.openWindowLanguage`).
     */
    public async choose(lang: string): Promise<void> {
        setLangPref(lang);
        const uid = this.userService.dbUser?.uid;
        if (uid) {
            try {
                await this.userService.updateLanguage(uid, lang);
            } catch (error) {
                // Die lokale Praeferenz gilt trotzdem; nur die Mail-Sprache bleibt alt.
                this.logger.warn('Could not persist language on dbUser', error);
            }
        }
    }

    public isDismissed(urlLang: string, targetLang: string): boolean {
        return isLangDismissed(urlLang, targetLang);
    }

    /** Ablehnung des Mismatch-Banners fuer genau dieses Sprachpaar merken. */
    public dismiss(urlLang: string, targetLang: string): void {
        setLangDismissed(urlLang, targetLang);
    }
}
